// Cart.js
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faPlus, 
  faMinus, 
  faTrash, 
  faArrowLeft,
  faMotorcycle,
  faClock,
  faGift,
  faTimes,
  faTag,
  faMapMarkerAlt,
  faEdit,
  faPlusCircle,
  faStore,
  faTruck
} from "@fortawesome/free-solid-svg-icons";
import { 
  updateItemQuantity, 
  setCartItems,
  clearCart,
  setResDetails
} from "../Utils/cartSlice";
import { authService } from "../services/authService";
import { cartService } from "../services/cartService";
import { 
  CART_API_URL, 
  CART_UPDATE_API_URL,
  API_BASE_URL, 
  ADDRESS_API_URL, 
  ADDRESS_DELETE_API_URL 
} from "../helpers/Constant";
import RestaurantMenuShimmering from "./RestaurantMenuShimmering";
import AddressDrawer from "./AddressDrawer";
import Toast from "./Toast";
import OrderTypeTabs from "./OrderTypeTabs";
import CartSkeleton from "./CartSkeleton";

const Cart = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cartItems = useSelector((store) => store.cart.cartItems);
  const resDetails = useSelector((store) => store.cart.resDetails);
  const [isCartLoading, setIsCartLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isTabSwitching, setIsTabSwitching] = useState(false);
  const [toast, setToast] = useState(null);
  const [loadingItems, setLoadingItems] = useState({});
  const authToken = authService.getToken();

  // Address state
  const [deliveryAddress, setDeliveryAddress] = useState(null);
  const [allAddresses, setAllAddresses] = useState([]);
  const [isAddressLoading, setIsAddressLoading] = useState(true);
  const [isAddressDrawerOpen, setIsAddressDrawerOpen] = useState(false);
  const [isSubmittingAddress, setIsSubmittingAddress] = useState(false);
  const [isDeletingAddress, setIsDeletingAddress] = useState(false);

  // Order details state - Default is Delivery (1)
  const [orderInstructions, setOrderInstructions] = useState("");
  const [tipAmount, setTipAmount] = useState(0);
  const [orderType, setOrderType] = useState(1);
  const [removeCoupon, setRemoveCoupon] = useState(false);
  const isUpdatingCartRef = useRef(false);

  // Bill details state from API
  const [billDetails, setBillDetails] = useState({
    subtotal: 0,
    discount: 0,
    gst_amount: 0,
    delivery_charge: 0,
    total_payable: 0,
    fees: []
  });

  // Address form fields
  const [addressForm, setAddressForm] = useState({
    label: 15,
    label_name: 'Home',
    address: '',
    apartment: '',
    area: '',
    landmark: '',
    lat: '',
    long: ''
  });

  // Coupon state
  const [couponCode, setCouponCode] = useState("");
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [isRemovingCoupon, setIsRemovingCoupon] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  // Toast helper
  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  const hideToast = useCallback(() => {
    setToast(null);
  }, []);

  // Load default delivery address
  const loadAddresses = useCallback(async () => {
    if (!authToken) {
      setIsAddressLoading(false);
      return;
    }

    try {
      const token = authService.getToken();
      const response = await fetch(ADDRESS_API_URL, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
      });

      const result = await response.json();
      if (result.status === 200 && result.data && result.data.length > 0) {
        setAllAddresses(result.data);
        const defaultAddr = result.data.find(addr => addr.is_default === 1) || result.data[0];
        setDeliveryAddress(defaultAddr);
        console.log("📍 Default address loaded:", defaultAddr);
      } else {
        setAllAddresses([]);
        setDeliveryAddress(null);
        console.log("📍 No address found");
      }
    } catch (error) {
      console.error("❌ Error loading address:", error);
      setAllAddresses([]);
      setDeliveryAddress(null);
    } finally {
      setIsAddressLoading(false);
    }
  }, [authToken]);

  // Handle address form change
  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setAddressForm(prev => ({ ...prev, [name]: value }));
  };

  // Submit new address
  const handleAddAddress = useCallback(async (e) => {
    e.preventDefault();
    let fullAddress = addressForm.address.trim();
    if (addressForm.area.trim()) fullAddress += `, ${addressForm.area.trim()}`;
    if (addressForm.landmark.trim()) fullAddress += `, ${addressForm.landmark.trim()}`;

    if (!fullAddress || !addressForm.apartment.trim()) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    setIsSubmittingAddress(true);
    try {
      const token = authService.getToken();
      const payload = {
        label: parseInt(addressForm.label),
        label_name: addressForm.label_name.toLowerCase(),
        address: fullAddress,
        apartment: addressForm.apartment.trim(),
        lat: addressForm.lat || '0',
        long: addressForm.long || '0',
        is_default: 1
      };

      const response = await fetch(ADDRESS_API_URL, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (result.data?.status === 200) {
        showToast('Address added successfully!', 'success');
        setAddressForm({
          label: 15,
          label_name: 'Home',
          address: '',
          apartment: '',
          area: '',
          landmark: '',
          lat: '',
          long: ''
        });
        setIsAddressDrawerOpen(false);
        await loadAddresses();
      } else {
        showToast(result.data?.message || 'Failed to add address', 'error');
      }
    } catch (error) {
      console.error("❌ Error adding address:", error);
      showToast('Network error. Please try again.', 'error');
    } finally {
      setIsSubmittingAddress(false);
    }
  }, [addressForm, loadAddresses, showToast]);

  // Delete address
  const handleDeleteAddress = useCallback(async () => {
    if (!deliveryAddress) return;
    if (!window.confirm('Are you sure you want to delete this address?')) return;

    setIsDeletingAddress(true);
    try {
      const token = authService.getToken();
      const response = await fetch(`${ADDRESS_DELETE_API_URL}/${deliveryAddress.id}`, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
      });

      const result = await response.json();
      if (result.data?.status === 200) {
        showToast('Address deleted successfully!', 'success');
        await loadAddresses();
      } else {
        showToast(result.data?.message || 'Failed to delete address', 'error');
      }
    } catch (error) {
      console.error("❌ Error deleting address:", error);
      showToast('Network error. Please try again.', 'error');
    } finally {
      setIsDeletingAddress(false);
    }
  }, [deliveryAddress, loadAddresses, showToast]);

  // Load cart data
  const loadCartData = useCallback(async () => {
    if (!authToken) {
      console.log("🔑 No auth token, showing empty cart");
      setIsCartLoading(false);
      dispatch(setCartItems([]));
      setAppliedCoupon(null);
      setDiscountAmount(0);
      setBillDetails({
        subtotal: 0,
        discount: 0,
        gst_amount: 0,
        delivery_charge: 0,
        total_payable: 0,
        fees: []
      });
      return;
    }

    try {
      console.log("📡 Fetching cart from API...");
      const cartData = await cartService.getCart();
      console.log("📦 Cart API response:", cartData);

      // Extract order type from response - default to 1 if not present
      if (cartData.order_type !== undefined && cartData.order_type !== null) {
        setOrderType(cartData.order_type);
      } else {
        setOrderType(1);
      }

      // Extract coupon data
      let couponCodeString = null;
      if (cartData.applied_coupon) {
        if (typeof cartData.applied_coupon === 'string') {
          couponCodeString = cartData.applied_coupon;
        } else if (typeof cartData.applied_coupon === 'object' && cartData.applied_coupon.code) {
          couponCodeString = cartData.applied_coupon.code;
        }
      }
      setAppliedCoupon(couponCodeString);

      // Extract bill details
      if (cartData.bill_details) {
        const bill = cartData.bill_details;
        setBillDetails({
          subtotal: bill.subtotal || 0,
          discount: bill.discount || 0,
          gst_amount: bill.gst_amount || 0,
          delivery_charge: bill.delivery_charge || 0,
          total_payable: bill.total_payable || 0,
          fees: bill.fees || []
        });
        setDiscountAmount(bill.discount || 0);
        
        if (bill.fees && Array.isArray(bill.fees)) {
          const tipFee = bill.fees.find(fee => fee.key === 'tip_amount');
          if (tipFee) {
            setTipAmount(tipFee.amount || 0);
          }
        }
      }

      // Process cart items
      if (cartData?.items && cartData.items.length > 0) {
        const transformedItems = cartData.items.map((item) => ({
          id: item.menu_item_id,
          cart_item_id: item.id,
          name: item.name,
          quantity: item.quantity,
          unit_price: parseFloat(item.unit_price) || 0,
          total_price: parseFloat(item.total_price) || 0,
          image: item.image || "",
          isVeg: item.variation_name ? item.variation_name.toLowerCase().includes("veg") : false,
          description: item.instructions || "",
        }));
        dispatch(setCartItems(transformedItems));

        if (cartData.restaurant_id) {
          dispatch(setResDetails({
            id: cartData.restaurant_id,
          }));
        }
        console.log("✅ Cart loaded with", transformedItems.length, "items");
      } else {
        console.log("📭 Cart is empty according to API");
        dispatch(setCartItems([]));
        setAppliedCoupon(null);
        setDiscountAmount(0);
        setBillDetails({
          subtotal: 0,
          discount: 0,
          gst_amount: 0,
          delivery_charge: 0,
          total_payable: 0,
          fees: []
        });
      }
    } catch (error) {
      console.error("❌ Error loading cart:", error);
      showToast('Failed to load cart, please refresh', 'error');
    } finally {
      setIsCartLoading(false);
      setIsTabSwitching(false);
    }
  }, [authToken, dispatch, showToast]);

  // Update cart API (menu item quantity)
  const updateCartAPI = useCallback(async (menuId, quantity, itemName) => {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new Error("No authentication token");
      }

      const payload = {
        menu_id: parseInt(menuId),
        quantity: parseInt(quantity)
      };

      console.log(`📤 API Call: ${itemName} -> Menu ID: ${menuId}, Quantity: ${quantity}`, payload);

      const response = await fetch(CART_API_URL, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const responseText = await response.text();
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error("❌ Failed to parse JSON:", parseError);
        throw new Error(`Invalid JSON response: ${responseText}`);
      }

      if (response.ok && result?.data?.status === 200) {
        const innerData = result.data.data;
        if (innerData?.items) {
          const transformedItems = innerData.items.map((item) => ({
            id: item.menu_item_id,
            cart_item_id: item.id,
            name: item.name,
            quantity: item.quantity,
            unit_price: parseFloat(item.unit_price) || 0,
            total_price: parseFloat(item.total_price) || 0,
            image: item.image || "",
            isVeg: item.variation_name ? item.variation_name.toLowerCase().includes("veg") : false,
            description: item.instructions || "",
          }));
          dispatch(setCartItems(transformedItems));
        }
        return { success: true };
      } else {
        return { 
          success: false, 
          error: result?.data?.message || result?.message || "Failed to update cart" 
        };
      }
    } catch (error) {
      console.error("❌ API Error:", error);
      return { 
        success: false, 
        error: error.message || "Network error" 
      };
    }
  }, [dispatch]);

  // Update cart details (address, instructions, tip, etc.)
  const updateCartDetails = useCallback(async (payload) => {
    if (isUpdatingCartRef.current) {
      console.log("⏳ Cart update already in progress, skipping...");
      return { success: false, error: "Update in progress" };
    }
    
    try {
      const token = authService.getToken();
      if (!token) throw new Error("No authentication token");

      isUpdatingCartRef.current = true;
      
      console.log("📤 Updating cart details with payload:", payload);
      
      const response = await fetch(CART_UPDATE_API_URL, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      console.log("📦 Cart update response:", result);
      
      if (result.data?.status === 200) {
        await loadCartData();
        return { success: true, data: result.data };
      } else {
        const errorMsg = result.data?.message || result?.message || "Failed to update cart";
        showToast(errorMsg, "error");
        return { success: false, error: errorMsg };
      }
    } catch (error) {
      console.error("❌ Error updating cart details:", error);
      showToast("Network error. Please try again.", "error");
      return { success: false, error: error.message };
    } finally {
      isUpdatingCartRef.current = false;
    }
  }, [authToken, loadCartData, showToast]);

  // Handle order type change with shimmer
  const handleOrderTypeChange = useCallback(async (type) => {
    if (type === orderType) return;
    
    // Show shimmer effect only for content area
    setIsTabSwitching(true);
    
    // Update order type immediately for UI feedback
    setOrderType(type);
    
    // Prepare payload based on order type
    const payload = {
      address_id: deliveryAddress?.id || null,
      order_type: type,
      remove_coupon: removeCoupon,
      order_instructions: orderInstructions.trim() || "",
      tip_amount: tipAmount,
    };

    console.log(`📤 Changing order type to ${type === 1 ? 'Delivery' : 'Pickup'}`, payload);
    
    const result = await updateCartDetails(payload);
    if (result.success) {
      showToast(`Switched to ${type === 1 ? 'Delivery' : 'Pickup'}`, 'success');
      // loadCartData will be called inside updateCartDetails
    }
    // isTabSwitching will be set to false in loadCartData finally block
  }, [orderType, deliveryAddress, removeCoupon, orderInstructions, tipAmount, updateCartDetails, showToast]);

  // Handle tip update
  const handleTipChange = useCallback(async (value) => {
    const tipValue = Number(value) || 0;
    setTipAmount(tipValue);
    
    if (authToken && deliveryAddress) {
      await updateCartDetails({
        address_id: deliveryAddress.id,
        order_type: orderType,
        remove_coupon: removeCoupon,
        order_instructions: orderInstructions,
        tip_amount: tipValue,
      });
    }
  }, [authToken, deliveryAddress, orderType, removeCoupon, orderInstructions, updateCartDetails]);

  // Auto‑update cart when address changes
  useEffect(() => {
    if (deliveryAddress && authToken) {
      updateCartDetails({
        address_id: deliveryAddress.id,
        order_type: orderType,
        remove_coupon: removeCoupon,
        order_instructions: orderInstructions,
        tip_amount: tipAmount,
      });
    }
  }, [deliveryAddress]);

  // Apply coupon
  const handleApplyCoupon = useCallback(async () => {
    if (!couponCode.trim()) {
      showToast('Please enter a coupon code', 'error');
      return;
    }
    if (!authToken) {
      showToast('Please login to apply coupon', 'error');
      return;
    }
    if (cartItems.length === 0) {
      showToast('Your cart is empty', 'error');
      return;
    }
    const restaurantId = resDetails.id;
    if (!restaurantId) {
      showToast('Restaurant information not available', 'error');
      return;
    }

    setIsApplyingCoupon(true);
    try {
      const token = authService.getToken();
      const payload = {
        coupon: couponCode.trim(),
        restaurantID: restaurantId
      };

      console.log("📤 Applying coupon with payload:", payload);

      const response = await fetch(`${API_BASE_URL}/cart/apply-coupon`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      console.log("📦 Coupon response:", result);

      if (response.ok) {
        let discount = 0;
        let couponApplied = false;
        
        if (result.data?.discount !== undefined && result.data?.discount !== null) {
          discount = parseFloat(result.data.discount) || 0;
        } else if (result.data?.data?.discount !== undefined && result.data?.data?.discount !== null) {
          discount = parseFloat(result.data.data.discount) || 0;
        } else if (result.discount !== undefined && result.discount !== null) {
          discount = parseFloat(result.discount) || 0;
        } else if (result.data?.amount !== undefined && result.data?.amount !== null) {
          discount = parseFloat(result.data.amount) || 0;
        }
        
        if (result.data?.applied_coupon) {
          couponApplied = true;
        } else if (result.data?.data?.applied_coupon) {
          couponApplied = true;
        } else if (result.applied_coupon) {
          couponApplied = true;
        } else if (result.data?.status === 200) {
          couponApplied = true;
        } else if (result.status === 200) {
          couponApplied = true;
        }

        if (couponApplied || discount > 0) {
          const message = discount > 0 
            ? `Coupon applied! You saved ₹${discount}`
            : 'Coupon applied successfully';
          
          showToast(message, 'success');
          setCouponCode('');
          setRemoveCoupon(false);
          await loadCartData();
        } else {
          const errorMsg = result.data?.message || 
                          result.message || 
                          'No discount available for this coupon';
          showToast(errorMsg, 'error');
          setAppliedCoupon(null);
          setDiscountAmount(0);
        }
      } else {
        const errorMsg = result.data?.message || 
                         result.message || 
                         result.error ||
                         'Failed to apply coupon. Please try again.';
        showToast(errorMsg, 'error');
        setAppliedCoupon(null);
        setDiscountAmount(0);
      }
    } catch (error) {
      console.error("❌ Error applying coupon:", error);
      showToast('Network error. Please try again.', 'error');
      setAppliedCoupon(null);
      setDiscountAmount(0);
    } finally {
      setIsApplyingCoupon(false);
    }
  }, [couponCode, authToken, cartItems.length, resDetails.id, showToast, loadCartData]);

  // Handle removing coupon
  const handleRemoveCoupon = useCallback(async () => {
    if (!appliedCoupon) {
      showToast('No coupon to remove', 'error');
      return;
    }

    if (!deliveryAddress) {
      showToast('Please add a delivery address first', 'error');
      return;
    }

    setIsRemovingCoupon(true);
    
    try {
      const payload = {
        address_id: deliveryAddress.id,
        order_type: orderType,
        remove_coupon: true,
        order_instructions: orderInstructions.trim() || "",
        tip_amount: tipAmount,
      };

      console.log("📤 Removing coupon with payload:", payload);

      const result = await updateCartDetails(payload);
      
      if (result.success) {
        setAppliedCoupon(null);
        setDiscountAmount(0);
        setCouponCode('');
        setRemoveCoupon(false);
        showToast('Coupon removed successfully', 'success');
        await loadCartData();
      } else {
        setRemoveCoupon(false);
        showToast(result.error || 'Failed to remove coupon', 'error');
      }
    } catch (error) {
      console.error("❌ Error removing coupon:", error);
      setRemoveCoupon(false);
      showToast('Failed to remove coupon. Please try again.', 'error');
    } finally {
      setIsRemovingCoupon(false);
    }
  }, [appliedCoupon, deliveryAddress, orderType, orderInstructions, tipAmount, updateCartDetails, loadCartData, showToast]);

  // Increment
  const handleIncrement = useCallback(async (item) => {
    const currentQuantity = item.quantity || 0;
    const newQuantity = currentQuantity + 1;

    setLoadingItems(prev => ({ ...prev, [item.id]: true }));

    try {
      dispatch(updateItemQuantity({ id: item.id, quantity: newQuantity }));

      const result = await updateCartAPI(item.id, newQuantity, item.name);
      
      if (!result.success) {
        dispatch(updateItemQuantity({ id: item.id, quantity: currentQuantity }));
        showToast(result.error || 'Failed to update cart', 'error');
      } else {
        showToast(`${item.name} added to cart`, 'success');
        await loadCartData();
      }
    } catch (error) {
      console.error("❌ Error incrementing item:", error);
      dispatch(updateItemQuantity({ id: item.id, quantity: currentQuantity }));
      showToast('Failed to update cart', 'error');
    } finally {
      setLoadingItems(prev => ({ ...prev, [item.id]: false }));
    }
  }, [dispatch, updateCartAPI, showToast, loadCartData]);

  // Decrement
  const handleDecrement = useCallback(async (item) => {
    const currentQuantity = item.quantity || 0;
    if (currentQuantity <= 0) return;

    const newQuantity = currentQuantity - 1;

    setLoadingItems(prev => ({ ...prev, [item.id]: true }));

    try {
      if (newQuantity === 0) {
        dispatch(updateItemQuantity({ id: item.id, quantity: 0 }));
        const result = await updateCartAPI(item.id, 0, item.name);
        if (result.success) {
          showToast(`${item.name} removed from cart`, 'error');
          await loadCartData();
        } else {
          dispatch(updateItemQuantity({ id: item.id, quantity: currentQuantity }));
          showToast(result.error || 'Failed to remove item', 'error');
        }
      } else {
        dispatch(updateItemQuantity({ id: item.id, quantity: newQuantity }));
        const result = await updateCartAPI(item.id, newQuantity, item.name);
        if (!result.success) {
          dispatch(updateItemQuantity({ id: item.id, quantity: currentQuantity }));
          showToast(result.error || 'Failed to update cart', 'error');
        } else {
          showToast(`${item.name} removed from cart`, 'error');
          await loadCartData();
        }
      }
    } catch (error) {
      console.error("❌ Error decrementing item:", error);
      dispatch(updateItemQuantity({ id: item.id, quantity: currentQuantity }));
      showToast('Failed to update cart', 'error');
    } finally {
      setLoadingItems(prev => ({ ...prev, [item.id]: false }));
    }
  }, [dispatch, updateCartAPI, showToast, loadCartData]);

  // Clear cart
  const handleClearCart = useCallback(async () => {
    if (cartItems.length === 0) return;
    if (!window.confirm('Are you sure you want to clear your cart?')) return;

    setIsProcessing(true);
    try {
      const result = await cartService.clearCart();
      if (result.success) {
        dispatch(clearCart());
        setAppliedCoupon(null);
        setDiscountAmount(0);
        setRemoveCoupon(false);
        setBillDetails({
          subtotal: 0,
          discount: 0,
          gst_amount: 0,
          delivery_charge: 0,
          total_payable: 0,
          fees: []
        });
        setTipAmount(0);
        showToast('Cart cleared successfully', 'success');
      } else {
        showToast('Failed to clear cart', 'error');
      }
    } catch (error) {
      console.error("❌ Error clearing cart:", error);
      showToast('Failed to clear cart', 'error');
    } finally {
      setIsProcessing(false);
    }
  }, [cartItems, dispatch, showToast]);

  // Checkout
  const handleCheckout = useCallback(async () => {
    if (cartItems.length === 0) return;
    if (orderType === 1 && !deliveryAddress) {
      showToast('Please add a delivery address first', 'error');
      return;
    }

    setIsProcessing(true);
    try {
      const payload = {
        address_id: deliveryAddress?.id || null,
        order_type: orderType,
        remove_coupon: removeCoupon,
        order_instructions: orderInstructions.trim() || "",
        tip_amount: tipAmount,
      };

      const result = await updateCartDetails(payload);
      if (result.success) {
        setTimeout(() => {
          setIsProcessing(false);
          navigate('/checkout');
        }, 500);
      } else {
        setIsProcessing(false);
      }
    } catch (error) {
      console.error("❌ Error during checkout:", error);
      showToast("Failed to proceed. Please try again.", "error");
      setIsProcessing(false);
    }
  }, [cartItems, deliveryAddress, orderType, removeCoupon, orderInstructions, tipAmount, updateCartDetails, navigate, showToast]);

  // Load data on mount
  useEffect(() => {
    loadCartData();
    loadAddresses();
  }, [loadCartData, loadAddresses]);

  // Show shimmer for initial load only - not for tab switching
  if (isCartLoading || isAddressLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-28">
        <RestaurantMenuShimmering />
      </div>
    );
  }

  // Empty cart
  if (!isCartLoading && cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 pt-28 flex items-center justify-center">
        <div className="text-center p-8">
          <div className="text-6xl mb-4">🛒</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Your cart is empty</h2>
          <p className="text-gray-600 mb-6">Looks like you haven't added any items to your cart yet.</p>
          <Link
            to="/"
            className="inline-block px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            Browse Restaurants
          </Link>
        </div>
      </div>
    );
  }

  // Main render
  return (
    <div className="min-h-screen bg-gray-50 pt-28">
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={hideToast} 
        />
      )}

      <div className="container mx-auto px-4 max-w-6xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-200 rounded-full transition-colors"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="text-xl" />
            </button>
            <h1 className="text-2xl font-bold text-gray-800">Your Cart</h1>
            <span className="text-sm text-gray-500 bg-gray-200 px-3 py-1 rounded-full">
              {cartItems.reduce((sum, item) => sum + item.quantity, 0)} items
            </span>
          </div>
          {cartItems.length > 0 && (
            <button
              onClick={handleClearCart}
              disabled={isProcessing}
              className="text-red-500 hover:text-red-700 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faTrash} />
              <span>Clear Cart</span>
            </button>
          )}
        </div>

        {/* Order Type Tabs */}
        <OrderTypeTabs 
          orderType={orderType}
          onTabChange={handleOrderTypeChange}
          isLoading={isTabSwitching || isProcessing}
        />

        {/* Show shimmer for content area only when switching tabs */}
        {isTabSwitching ? (
          <CartSkeleton />
        ) : (
          <>
            {resDetails?.resName && (
              <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                <div className="flex items-center gap-3">
                  <img
                    src={resDetails.resImg || "https://via.placeholder.com/60x60?text=Restaurant"}
                    alt={resDetails.resName}
                    className="w-12 h-12 rounded-lg object-cover"
                    onError={(e) => {
                      e.target.src = "https://via.placeholder.com/60x60?text=Restaurant";
                    }}
                  />
                  <div>
                    <h3 className="font-bold text-lg text-gray-800">{resDetails.resName}</h3>
                    <p className="text-sm text-gray-500">{resDetails.resArea}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Show delivery address only for delivery orders */}
            {orderType === 1 && (
              <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="text-orange-500 text-lg mt-1" />
                    <div className="flex-1">
                      {deliveryAddress ? (
                        <>
                          <p className="text-sm font-medium text-gray-700">Delivery Address</p>
                          <p className="text-sm text-gray-600">
                            {deliveryAddress.apartment}, {deliveryAddress.address}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {deliveryAddress.label_name?.toUpperCase() || 'Home'} Address
                          </p>
                        </>
                      ) : (
                        <p className="text-sm text-gray-500">No address added yet. Please add one.</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {deliveryAddress && (
                      <button
                        onClick={handleDeleteAddress}
                        disabled={isDeletingAddress}
                        className="text-red-500 hover:text-red-700 transition-colors disabled:opacity-50"
                        title="Delete this address"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    )}
                    <button
                      onClick={() => setIsAddressDrawerOpen(true)}
                      className="text-orange-500 hover:text-orange-600 transition-colors flex items-center gap-1 text-sm"
                    >
                      <FontAwesomeIcon icon={faPlusCircle} />
                      {deliveryAddress ? 'Change' : 'Add Address'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Show pickup info for pickup orders */}
            {orderType === 2 && (
              <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                <div className="flex items-start gap-3">
                  <FontAwesomeIcon icon={faStore} className="text-orange-500 text-lg mt-1" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-700">Pickup Order</p>
                    <p className="text-sm text-gray-600">
                      You will pick up your order from the restaurant
                    </p>
                    {resDetails?.resName && (
                      <p className="text-xs text-gray-400 mt-1">
                        {resDetails.resName} - {resDetails.resArea}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                  {cartItems.map((item) => {
                    const isLoading = loadingItems[item.id];
                    const itemTotal = (item.unit_price || item.price || 0) * (item.quantity || 0);

                    return (
                      <div
                        key={item.id}
                        className="flex items-center gap-4 p-4 border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-20 h-20 flex-shrink-0">
                          <img
                            src={item.image || "https://via.placeholder.com/80x80?text=No+Image"}
                            alt={item.name}
                            className="w-full h-full rounded-lg object-cover"
                            onError={(e) => {
                              e.target.src = "https://via.placeholder.com/80x80?text=No+Image";
                            }}
                          />
                        </div>

                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-800 truncate">{item.name}</h4>
                          <p className="text-sm text-gray-500 truncate">{item.description || "Delicious food"}</p>
                          <p className="text-lg font-bold text-orange-600">
                            ₹{(item.unit_price || item.price || 0).toFixed(0)}
                          </p>
                        </div>

                        <div className="flex flex-col items-end gap-2">
                          <div className="flex items-center gap-2 bg-white border border-green-600 rounded-lg">
                            <button
                              onClick={() => handleDecrement(item)}
                              disabled={isLoading || isProcessing}
                              className={`w-8 h-8 flex items-center justify-center text-green-600 
                                hover:bg-green-50 rounded-l-lg active:scale-95 transform
                                ${(isLoading || isProcessing) ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              <FontAwesomeIcon icon={faMinus} />
                            </button>

                            <span className="text-green-600 font-semibold min-w-[20px] text-center select-none">
                              {isLoading ? '...' : item.quantity}
                            </span>

                            <button
                              onClick={() => handleIncrement(item)}
                              disabled={isLoading || isProcessing}
                              className={`w-8 h-8 flex items-center justify-center text-green-600 
                                hover:bg-green-50 rounded-r-lg active:scale-95 transform
                                ${(isLoading || isProcessing) ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              <FontAwesomeIcon icon={faPlus} />
                            </button>
                          </div>
                          <span className="text-sm font-semibold text-gray-700">
                            ₹{itemTotal.toFixed(0)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="lg:col-span-1">
                <div className="bg-white rounded-lg shadow-sm p-6 sticky top-28">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Order Summary</h3>
                  
                  <div className="space-y-3 border-b border-gray-200 pb-4">
                    <div className="flex justify-between text-gray-600">
                      <span>Subtotal ({cartItems.reduce((sum, item) => sum + item.quantity, 0)} items)</span>
                      <span>₹{billDetails.subtotal.toFixed(0)}</span>
                    </div>
                    
                    {billDetails.fees && billDetails.fees.length > 0 && 
                      billDetails.fees.map((fee, index) => (
                        <div key={index} className="flex justify-between text-gray-600">
                          <span>{fee.label || fee.key}</span>
                          <span>₹{fee.amount.toFixed(0)}</span>
                        </div>
                      ))
                    }
                    
                    {billDetails.gst_amount > 0 && (
                      <div className="flex justify-between text-gray-600">
                        <span>GST</span>
                        <span>₹{billDetails.gst_amount.toFixed(0)}</span>
                      </div>
                    )}
                    
                    <div className="flex justify-between text-gray-600">
                      <span>Delivery Charge</span>
                      <span className={billDetails.delivery_charge === 0 ? 'text-green-600' : ''}>
                        {billDetails.delivery_charge === 0 ? 'Free' : `₹${billDetails.delivery_charge.toFixed(0)}`}
                      </span>
                    </div>
                    
                    {billDetails.delivery_charge > 0 && orderType === 1 && (
                      <div className="text-xs text-gray-400">
                        Add items worth ₹{(500 - billDetails.subtotal).toFixed(0)} more for free delivery
                      </div>
                    )}

                    {discountAmount > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>Coupon Discount</span>
                        <span>- ₹{discountAmount.toFixed(0)}</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-4">
                    {appliedCoupon ? (
                      <div className="flex items-center justify-between bg-green-50 p-2 rounded-lg">
                        <div className="flex items-center gap-2">
                          <FontAwesomeIcon icon={faTag} className="text-green-600" />
                          <span className="text-sm font-medium text-green-700">{appliedCoupon}</span>
                        </div>
                        <button
                          onClick={handleRemoveCoupon}
                          disabled={isRemovingCoupon}
                          className={`text-red-500 hover:text-red-700 transition-colors px-2 py-1 text-sm ${
                            isRemovingCoupon ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          <FontAwesomeIcon icon={faTimes} className="mr-1" />
                          {isRemovingCoupon ? 'Removing...' : 'Remove'}
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Enter coupon code"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value)}
                          className="flex-1 p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                          disabled={isApplyingCoupon}
                        />
                        <button
                          onClick={handleApplyCoupon}
                          disabled={isApplyingCoupon || !couponCode.trim()}
                          className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                            isApplyingCoupon || !couponCode.trim()
                              ? 'bg-gray-300 cursor-not-allowed'
                              : 'bg-orange-500 text-white hover:bg-orange-600'
                          }`}
                        >
                          {isApplyingCoupon ? 'Applying...' : 'Apply'}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Order Instructions</label>
                    <textarea
                      rows="2"
                      value={orderInstructions}
                      onChange={(e) => setOrderInstructions(e.target.value)}
                      placeholder={orderType === 1 ? "e.g., leave at the door, ring the bell" : "e.g., call when ready for pickup"}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>

                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Add Tip (₹)</label>
                    <input
                      type="number"
                      min="0"
                      value={tipAmount}
                      onChange={(e) => handleTipChange(e.target.value)}
                      placeholder="e.g., 50"
                      className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">Tip amount will be added to your order total</p>
                  </div>

                  <div className="flex justify-between py-4 text-lg font-bold text-gray-800 border-t border-gray-200 mt-4">
                    <span>Total</span>
                    <span>₹{billDetails.total_payable.toFixed(0)}</span>
                  </div>

                  <button
                    onClick={handleCheckout}
                    disabled={cartItems.length === 0 || isProcessing || (orderType === 1 && !deliveryAddress)}
                    className={`w-full py-3 rounded-lg font-semibold transition-colors
                      ${cartItems.length === 0 || isProcessing || (orderType === 1 && !deliveryAddress)
                        ? 'bg-gray-300 cursor-not-allowed'
                        : 'bg-orange-500 text-white hover:bg-orange-600'
                      }`}
                  >
                    {isProcessing ? 'Processing...' : `Proceed to ${orderType === 1 ? 'Checkout' : 'Pickup'}`}
                  </button>

                  {orderType === 1 && !deliveryAddress && (
                    <p className="text-xs text-red-500 mt-2">Please add a delivery address to proceed.</p>
                  )}

                  <div className="mt-4 space-y-2 text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      <FontAwesomeIcon icon={orderType === 1 ? faTruck : faStore} className="text-gray-400" />
                      <span>
                        {orderType === 1 
                          ? 'Free delivery on orders above ₹500' 
                          : 'Pick up your order from the restaurant'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FontAwesomeIcon icon={faClock} className="text-gray-400" />
                      <span>{orderType === 1 ? 'Estimated delivery: 30-40 mins' : 'Estimated pickup: 15-20 mins'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FontAwesomeIcon icon={faGift} className="text-gray-400" />
                      <span>Offers and discounts applied at checkout</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <AddressDrawer
        isOpen={isAddressDrawerOpen}
        onClose={() => setIsAddressDrawerOpen(false)}
        addressForm={addressForm}
        onAddressChange={handleAddressChange}
        onSubmit={handleAddAddress}
        isSubmitting={isSubmittingAddress}
      />
    </div>
  );
};

export default Cart;